import React, { useEffect, useState } from 'react';
import * as ReactDOM from 'react-dom';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Icons, Row } from '../../../components/base/BootstrapComponents/BootstrapComponents';
import { errorNotification, successNotification } from '../../Notifications/notificationDecorator';
import { getResponseError, setFormError } from '../../../utils/utils';
import { FORM_VALIDATION_MESSAGE } from '../../../utils/apiError';
import { createRules, editRulesById } from '../actions';
import Loader from '../../../components/base/Loader/Loader';
import { getRulesData } from '../reducer';
import RulesDTO, {
  getRuleStateFromStates,
  normalizeRuleMessageForForm,
  normalizeRuleStates,
  validateRuleBounds,
  validateRuleName,
  validateRuleType,
} from '../../../dto/RulesDTO';
import FieldDTOWithCol from '../../../components/fields/Abstract/FieldDTOWithCol';
import TextInputWithLabel from '../../../components/fields/TextInput/TextInputWithLabel';
import CustomSelectInput from '../../../components/fields/SelectInput/CustomSelectInput';
import {
  DAYS_LIST,
  PROPULSION_TYPES_LIST, RATE_APPLIES_WHEN_LIST, RATE_RECURRENCES_LIST,
  RULE_UNITS_LIST,
  RULES_TYPE_LIST,
  VEHICLE_STATE_LIST,
  VEHICLE_TYPES_LIST,
} from '../../../configs';
import Switches from '../../../components/fields/Switches/Switches';
import TimePicker from '../../../components/fields/TimePicker/TimePicker';
import CustomSelectInputWithHelper from '../../../components/fields/SelectInput/CustomSelectInputWithHelper';
import { loadZoneList } from '../../Zones/actions';
import { loadGeographyList } from '../../Geography/actions';
import SelectGeographies from './SelectGeographies';

const ruleStateTransform = {
  output: e => e?.target?.value || '',
  input: value => (value && typeof value === 'object' ? getRuleStateFromStates(value) : value || ''),
};

const toNullableNumber = value => {
  if (value === '' || value === null || typeof value === 'undefined') {
    return null;
  }
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
};

const toFormTextValue = value => (value === null || typeof value === 'undefined' ? '' : value);

const normalizeRuleFormDefaultValues = currentRules => ({
  ...currentRules,
  message: normalizeRuleMessageForForm(currentRules.message),
  rule_units: toFormTextValue(currentRules.rule_units),
  minimum: toFormTextValue(currentRules.minimum),
  maximum: toFormTextValue(currentRules.maximum),
  rate_amount: toFormTextValue(currentRules.rate_amount),
  rate_recurrence: toFormTextValue(currentRules.rate_recurrence),
  rate_applies_when: toFormTextValue(currentRules.rate_applies_when),
  start_time: toFormTextValue(currentRules.start_time),
  end_time: toFormTextValue(currentRules.end_time),
  value_url: toFormTextValue(currentRules.value_url),
});

export default function RulesForm(props) {
  const {
    onClose = () => {},
    loadData = () => {},
    loadRules = () => {},
    currentRules = null,
  } = props;
  const { loadingCreateRules, loadingEditRulesById } = useSelector(getRulesData);
  const dispatch = useDispatch();
  const defaultValues = currentRules ? normalizeRuleFormDefaultValues(currentRules) : {};
  const {
    control, getValues, setError, clearErrors,
  } = useForm({
    mode: 'onChange',
    defaultValues,
  });

  const handleSave = () => {
    clearErrors();
    const values = { ...getValues() };
    values.name = typeof values.name === 'string' ? values.name.trim() : '';
    const nameError = validateRuleName(values.name);
    if (nameError && typeof nameError === 'string') {
      setError('name', { type: 'manual', message: nameError }, { shouldFocus: true });
      errorNotification('Сохранение формы', FORM_VALIDATION_MESSAGE);
      return;
    }
    const typeError = validateRuleType(values.rule_type);
    if (typeError && typeof typeError === 'string') {
      setError('rule_type', { type: 'manual', message: typeError }, { shouldFocus: true });
      errorNotification('Сохранение формы', FORM_VALIDATION_MESSAGE);
      return;
    }
    const minimum = toNullableNumber(values.minimum);
    const maximum = toNullableNumber(values.maximum);
    const boundsError = validateRuleBounds(minimum, maximum);
    if (boundsError && typeof boundsError === 'string') {
      setError('maximum', { type: 'manual', message: boundsError }, { shouldFocus: true });
      errorNotification('Сохранение формы', FORM_VALIDATION_MESSAGE);
      return;
    }
    try {
      const states = normalizeRuleStates(values.states);
      if (!states) {
        setError('states', { type: 'manual', message: 'Выберите состояние ТС' }, { shouldFocus: true });
        errorNotification('Сохранение формы', FORM_VALIDATION_MESSAGE);
        return;
      }
      values.states = states;
    } catch (e) {
      setError('states', { type: 'manual', message: e.message || 'Выберите корректное состояние ТС' }, { shouldFocus: true });
      errorNotification('Сохранение формы', FORM_VALIDATION_MESSAGE);
      return;
    }
    const action = currentRules?.rule_id ? editRulesById({ ...values, id: currentRules.rule_id }) : createRules(values);
    dispatch(action)
      .then(rules => {
        successNotification('Сохранение формы', 'Форма сохранена');
        loadData();
        // if (!currentRules?.rule_id && rules?.rule_id) {
        //   loadRules(rules.rule_id);
        // }
        onClose();
      })
      .catch(response => {
        setFormError(response, setError);
        errorNotification('Сохранение формы', getResponseError(response));
      });
  };

  const dto = new RulesDTO();
  const attributes = dto.getAttributesByName();

  return ReactDOM.createPortal(
    <div className="form-dialog rules-form">
      <Loader loading={loadingCreateRules || loadingEditRulesById} />
      <div className="form-header">
        <div className="breadcrumbs">
          <span className="breadcrumbs-prev">Правила</span>
          <Icons width={22} height={22} customIcon icon="arrow-right" className="breadcrumbs-arrow" />
          <span className="breadcrumbs-current">Правило № {currentRules?.rule_id || 'Без номера'}</span>
          <span className="breadcrumbs-current"> ({currentRules?.rule_id ? 'редактирование' : 'создание'}) </span>

        </div>
        <div className="close-button" onClick={onClose}>
          <Icons width={24} height={24} customIcon icon="close" />
        </div>
      </div>
      <div className="form-body">
        <form className="form-content">
          <div className="form-fields">
            <div className="form-fields-wrapper custom-scroll-theme">
              <div className="afc-body">
                <div className="afc-main rules-main-info">
                  <Row className="row-cols-1">
                    <FieldDTOWithCol
                      control={control}
                      type="text"
                      component={TextInputWithLabel}
                      dtoObject={attributes.name}
                      variant="material"
                      withLabel
                      className="mb-3"
                    />
                    <FieldDTOWithCol
                      control={control}
                      dtoObject={attributes.geographies}
                      usePortal
                      // addPlaceholder
                      component={SelectGeographies}
                      portalClassName="executor-selector"
                      loadAction={loadGeographyList}
                      optionFormatter={item => ({ value: item.geography_id, name: item.name })}
                      filterName="full_name"
                      variant="material"
                      withLabel
                      className="mb-3"
                      multiple
                    />
                    <FieldDTOWithCol
                      control={control}
                      type="text"
                      component={CustomSelectInput}
                      dtoObject={attributes.rule_type}
                      variant="material"
                      withLabel
                      options={RULES_TYPE_LIST}
                      className="mb-3"
                    />
                    <FieldDTOWithCol
                      control={control}
                      type="text"
                      component={CustomSelectInput}
                      dtoObject={attributes.rule_units}
                      variant="material"
                      withLabel
                      options={RULE_UNITS_LIST}
                      className="mb-3"
                    />
                    <FieldDTOWithCol
                      control={control}
                      type="text"
                      component={CustomSelectInput}
                      dtoObject={attributes.vehicle_types}
                      variant="material"
                      withLabel
                      options={VEHICLE_TYPES_LIST}
                      className="mb-3"
                      multiple
                    />
                    <FieldDTOWithCol
                      control={control}
                      type="text"
                      component={CustomSelectInput}
                      dtoObject={attributes.propulsion_types}
                      variant="material"
                      withLabel
                      options={PROPULSION_TYPES_LIST}
                      className="mb-3"
                      multiple
                    />
                    <FieldDTOWithCol
                      control={control}
                      type="text"
                      component={TextInputWithLabel}
                      dtoObject={attributes.minimum}
                      variant="material"
                      withLabel
                      className="mb-3"
                    />
                    <FieldDTOWithCol
                      control={control}
                      type="text"
                      component={TextInputWithLabel}
                      dtoObject={attributes.maximum}
                      variant="material"
                      withLabel
                      className="mb-3"
                    />
                    <FieldDTOWithCol
                      control={control}
                      type="text"
                      component={Switches}
                      dtoObject={attributes.inclusive_minimum}
                      variant="material"
                      withLabel
                      className="mb-3"
                    />
                    <FieldDTOWithCol
                      control={control}
                      type="text"
                      component={Switches}
                      dtoObject={attributes.inclusive_maximum}
                      variant="material"
                      withLabel
                      className="mb-3"
                    />
                  </Row>
                </div>
                <div className="afc-add">
                  <Row className="row-cols-1">
                    <FieldDTOWithCol
                      control={control}
                      type="text"
                      component={TextInputWithLabel}
                      dtoObject={attributes.rate_amount}
                      variant="material"
                      withLabel
                      className="mb-3"
                    />
                    <FieldDTOWithCol
                      control={control}
                      type="text"
                      component={CustomSelectInput}
                      dtoObject={attributes.rate_recurrence}
                      variant="material"
                      withLabel
                      options={RATE_RECURRENCES_LIST}
                      className="mb-3"
                    />
                    <FieldDTOWithCol
                      control={control}
                      type="text"
                      component={CustomSelectInput}
                      dtoObject={attributes.rate_applies_when}
                      variant="material"
                      withLabel
                      options={RATE_APPLIES_WHEN_LIST}
                      className="mb-3"
                    />
                    <FieldDTOWithCol
                      control={control}
                      type="text"
                      component={TimePicker}
                      dtoObject={attributes.start_time}
                      variant="material"
                      withLabel
                      className="mb-3"
                      columns={6}
                    />
                    <FieldDTOWithCol
                      control={control}
                      type="text"
                      component={TimePicker}
                      dtoObject={attributes.end_time}
                      variant="material"
                      withLabel
                      className="mb-3"
                      columns={6}
                    />
                    <FieldDTOWithCol
                      control={control}
                      type="text"
                      component={CustomSelectInput}
                      dtoObject={attributes.days}
                      variant="material"
                      withLabel
                      options={DAYS_LIST}
                      multiple
                      className="mb-3"
                    />
                    <FieldDTOWithCol
                      control={control}
                      type="text"
                      component={CustomSelectInput}
                      dtoObject={attributes.states}
                      variant="material"
                      withLabel
                      options={VEHICLE_STATE_LIST}
                      addPlaceholder
                      placeholder="Выберите состояние ТС"
                      className="mb-3"
                      transform={ruleStateTransform}
                    />
                    <FieldDTOWithCol
                      control={control}
                      type="text"
                      component={TextInputWithLabel}
                      dtoObject={attributes.message}
                      variant="material"
                      withLabel
                      textarea
                      className="mb-3"
                    />
                    <FieldDTOWithCol
                      control={control}
                      type="text"
                      component={TextInputWithLabel}
                      dtoObject={attributes.value_url}
                      variant="material"
                      withLabel
                      textarea
                      className="mb-3"
                    />
                  </Row>
                </div>
              </div>
            </div>
          </div>
          <div className="form-footer">
            <Button onClick={() => handleSave()}>{currentRules?.rule_id ? 'Сохранить' : 'Создать'}</Button>
            <Button variant="secondary" onClick={onClose}>Отменить</Button>
          </div>
        </form>
      </div>
    </div>,
    document.getElementById('app-content'),
  );
}
